// OverlayPanelController — Faithful-Extraktion aus basic.js `$.fn.overlayControls2` (Slice 2b-1).
//
// Holt get_overlays, rendert die Overlays (update_ui: flat → Quad-/Ground-Layer, getilt → overlayMapTypes ImageMapType)
// und verdrahtet das OV-Panel (Button + Liste/Auge/Tools).
//
// Slice 2 (State-Objekt): Wahrheit ist jetzt der **MapStateStore** (View-State: visible/opacity), nicht mehr die Closure-
// `overlays[]`. Stammdaten (DB) liegen in `byId`; `update_ui` rendert AUS dem Store ⋈ byId; Auge/Opacity schreiben in den
// Store → „changed" → update_ui (unidirektional). Render-Logik selbst 1:1 wie zuvor. Nebeneffekt: der View ist als reines
// Objekt **inspizierbar** (Observability) — Debug-Handle folgt in Slice 4 (Store dann zentral pro Kontext). De-jQuery später.
//
// Standalone nutzbar (auch ohne Reconciler/api) — daher google + map + opts explizit. opts:
//   showAll        — get_overlays mit &all=1 (Admin)
//   hidden         — initial nichts zeigen (show=false)
//   ovSide         — 'left' | 'right' (Panel-Seite)
//   onReady(show)  — Callback nach Aufbau (ersetzt das frühere callback.call($el, show))

import { createGroundOverlayLayerClass } from "./GroundOverlayLayer.js";
import { createQuadOverlayLayerClass } from "./QuadOverlayLayer.js";
import { createOverlayControlsClass } from "./OverlayControlsPanel.js";
import { MapStateStore, createDefaultMapState } from "../state/MapStateStore.js";
import { wmtsLayersForArchive, buildWmtsImageMapType } from "./wmtsLayers.js";
import { str } from "../../i18n/str.js";

// Listen-Gruppen: externe Kartenwerke zuerst, dann die Archiv-Overlays. Der Rang sortiert nur —
// die Beschriftung liefert der Controller mit (die View bleibt dumm).
const GROUP_EXTERN = 'extern';
const GROUP_ARCHIVE = 'archive';
const GROUP_RANK = { [GROUP_EXTERN]: 0, [GROUP_ARCHIVE]: 1 };

// Stammdaten (DB) + View-State (Store) zu einem Render-Objekt mergen. opacity/visible kommen aus dem Store und steuern
// Anzeige/Liste; alles andere (display_mode, corners, bounds, path, rotation, sort_order, zoom_*) ist Stammdatum.
function mergeViewState(stamm, viewState) {
    var merged = Object.assign({}, stamm, { visible: viewState.visible, opacity: viewState.opacity });
    // Frontend-Sortierung (Chef-Wunsch 9, memory): View-State sortOrder überschreibt die Stammdaten-sort_order, wenn gesetzt.
    if (viewState.sortOrder != null) { merged.sort_order = viewState.sortOrder; }
    return merged;
}


export function createOverlayPanel(google, map, opts = {}) {
    if (!map) { return { destroy() {} }; }
    const $ = window.$;   // jQuery global (Legacy-Seite) — De-jQuery später

    // Layer PRO KARTE wiederverwenden (sonst Doppel-Overlay beim View-Wechsel). arrangeOverlays.retain räumt auf;
    // switchTo leert über map.__topoFlatLayer / map.__topoQuadLayer.
    var flatLayer = map.__topoFlatLayer || new (createGroundOverlayLayerClass(google))(map);
    if (flatLayer) { map.__topoFlatLayer = flatLayer; }
    var quadLayer = map.__topoQuadLayer || new (createQuadOverlayLayerClass(google))(map);
    if (quadLayer) { map.__topoQuadLayer = quadLayer; }
    // Getilte Overlays (ImageMapType) ebenfalls pro Karte nach Id cachen → Opacity-Änderung = nur setOpacity statt
    // remove+re-add (sonst luden die Tiles bei jedem update_ui neu = Flackern). id → google.maps.ImageMapType.
    var tiledItems = map.__topoTiledOverlays || (map.__topoTiledOverlays = new Map());

    // Teardown-Refs fürs Reconciler-Handle. Der basic.js-Wrapper ignoriert das Handle → dort Verhalten unverändert
    // (inkl. des bestehenden Listener-Leaks pro Lauf). Der Reconciler ruft destroy() → sauberer Abbau.
    let mapTypeListener = null;
    let unsubscribe = null;
    let destroyed = false;

    // Stammdaten (DB-Wahrheit) im ÄUSSEREN Scope, nicht mehr im ajax-done: sie sollen austauschbar sein, ohne die
    // Verdrahtung daneben ein zweites Mal aufzubauen. `update_ui` und `buildItems` lesen sie über DIESE Bindung —
    // ein Neuzuweisen unten wirkt damit auch in den bereits angemeldeten Closures.
    var stamm = [];
    var byId  = {};

    // View-State (visible/opacity) im Store. Slice 3: zentraler Store kommt via opts.store (von MapController) —
    // Fallback lokal (z.B. document_locate). Hierher gezogen, damit das Seeding unten eine eigene Funktion sein kann.
    var store = opts.store || new MapStateStore(createDefaultMapState());

    // View-State auf die aktuellen Stammdaten legen. `keepAll` behält den Zustand JEDER weiterhin vorhandenen Id;
    // beim Aufbau entscheidet das wie bisher die Einstellung `keepOnPositionChange` („Keep settings"), pro Kontext
    // eigener Store → kein Bleed. Overlays, die aus den Stammdaten gefallen sind, verschwinden damit auch aus dem
    // Store — das Rendern nimmt sie daraufhin von der Karte (retain/reconcile in update_ui).
    function seedStore(keepAll) {
        var prevVS = {};
        store.get().overlays.forEach(function (vs) { prevVS[vs.id] = vs; });
        var keep = keepAll || !!store.get().keepOnPositionChange;
        store.setOverlays(stamm.map(function (o) {
            var p = keep ? prevVS[o.id] : null;
            return { id: o.id, visible: p ? p.visible : false, opacity: (p ? p.opacity : 1), sortOrder: o.sort_order };
        }));
    }

    // json (get_overlays) → stamm/byId. Ersetzt den Inhalt, legt keine neuen Bindungen an. Liefert `show`:
    // gibt es mindestens ein Overlay ohne `linked_to` (= eines, das der Nutzer selbst ein-/ausblenden kann)?
    function applyStammFromResponse(json) {
        var show = false;
        stamm = [];
        byId  = {};
        for (var i in json.data) {
            var s = json.data[i];
            if (!s['linked_to']) { show = true; }
            stamm.push(s);
            byId[s.id] = s;
        }
        stamm.sort(function (a, b) { return b.year - a.year; });

        // Externe WMTS-Kartenwerke des Archivs als synthetische Overlays einreihen (Registry: wmtsLayers.js).
        // Gleiche Item-Form wie die DB-Zeilen → Store, Liste, Auge, Deckkraft und „zentrieren" laufen unverändert;
        // nur das Rendern kennt den Sonderfall (source:'wmts' in update_ui). Die id ist bewusst ein Präfix-String
        // ('wmts:<key>') → kollidiert nie mit einer DB-Id und ist im Store-Snapshot als extern erkennbar.
        // Die Archiv-Id kommt aus DIESER Antwort (ajax.php get_overlays) — sonst hat der Client sie nirgends
        // synchron, und so gilt derselbe Weg in allen Kontexten (main/gallery/locate/settings).
        wmtsLayersForArchive(json.archive_id).forEach(function (cfg) {
            var w = Object.assign({
                id: 'wmts:' + cfg.key,
                name: cfg.name,
                year: null,
                info_text: null,
                source: 'wmts',
                wmts: cfg,
                sort_order: 0
            }, cfg.bounds || {});   // bounds in get_overlays-Feldnamen → jumpToOverlay greift ohne Sonderfall
            stamm.push(w);
            byId[w.id] = w;
        });
        return show;
    }

    $.ajax({
        url: '/includes/ajax.php',
        cache: false,
        type: 'POST',
        data: 'action=get_overlays' + (opts.showAll ? '&all=1' : ''),
        dataType: 'json'
    }).done(function (json) {
        if (destroyed) { return; }   // vor dem Laden zerstört → nichts aufbauen
        if (json.success && json.data) {
            // Stammdaten (DB-Wahrheit) aus get_overlays — year-sortiert; per id für den Join im Renderer (byId).
            var show = applyStammFromResponse(json);

            if (opts.hidden == true) { show = false; }

            function update_ui() {
                var state = store.get();
                var order = [];

                // 1) linked_to-Overlays passend zum aktuellen Map-Typ (Stammdaten) — unabhängig vom View-State `visible`.
                for (var a = 0; a < state.overlays.length; a++) {
                    var vsA = state.overlays[a];
                    var oA = byId[vsA.id];
                    if (oA && oA.linked_to && oA.linked_to.toLowerCase() == map.getMapTypeId().toLowerCase()) {
                        if (!window.dynamicMapSwitchingPaused) { window.dynamicMapSwitchingPaused = true; }
                        order.push(mergeViewState(oA, vsA));
                    }
                }
                // 2) sichtbare Overlays (View-State aus dem Store).
                for (var b = 0; b < state.overlays.length; b++) {
                    var vsB = state.overlays[b];
                    if (vsB.visible) {
                        var oB = byId[vsB.id];
                        if (oB) { order.push(mergeViewState(oB, vsB)); }
                    }
                }

                // (Früher hier: ALLE overlayMapTypes entfernen und im Loop neu aufbauen → getilte Overlays luden bei
                //  JEDEM update_ui ihre Tiles neu = Flackern, v.a. beim Opacity-Slider. Jetzt: getilte über tiledItems
                //  reconcilen — bestehende behalten + nur setOpacity, nicht mehr sichtbare am Ende entfernen.)
                var flatVisible = [];
                var quadVisible = [];
                var tiledVisible = [];
                for (var id in order) {
                    (function (i) {
                        // Externes WMTS-Kartenwerk: wie die getilten Overlays pro Id cachen (Deckkraft = setOpacity,
                        // kein Tile-Neuladen) — aber UNTEN einhängen (insertAt 0), es ist eine Basiskarte und die
                        // Archiv-Overlays gehören darüber. Das Entfernen macht der tiledItems-Reconcile weiter unten
                        // per Identität (löst das frühere blinde overlayMapTypes.removeAt(0) im Adapter ab).
                        if (order[i].source === 'wmts') {
                            var wid = String(order[i].id);
                            tiledVisible.push(wid);
                            var cachedW = tiledItems.get(wid);
                            if (cachedW) { cachedW.setOpacity(order[i].opacity != null ? order[i].opacity : 1); return; }
                            var wmtsImt = buildWmtsImageMapType(google, order[i].wmts, order[i].opacity);
                            map.overlayMapTypes.insertAt(0, wmtsImt);
                            tiledItems.set(wid, wmtsImt);
                            return;
                        }
                        // Flach (ohne Tiles): ALLE über QuadOverlayLayer (matrix3d) → einheitlich + z-Order (sort_order).
                        // Fehlt der Quad-Layer → Fallback GroundOverlay (ohne z-Order).
                        if ((quadLayer || flatLayer) && order[i].display_mode === 'flat') {
                            var o = order[i];
                            var fOpacity = o.opacity != null ? o.opacity : 1;
                            if (quadLayer) {
                                // distort vorhanden → echtes Quad; sonst achsenparallele Box aus den Bounds.
                                var corners = o.distort_corners || {
                                    nw: { lat: o.lat_northeast, lng: o.lng_southwest },
                                    ne: { lat: o.lat_northeast, lng: o.lng_northeast },
                                    se: { lat: o.lat_southwest, lng: o.lng_northeast },
                                    sw: { lat: o.lat_southwest, lng: o.lng_southwest }
                                };
                                quadLayer.ensure(o.id, {
                                    url: '/' + o.path + o.filename,
                                    corners: corners,
                                    rotation: o.rotation || 0,
                                    opacity: fOpacity,
                                    zIndex: 1000 - (o.sort_order || 0)   // sort_order 0 = oben = vorne
                                });
                                quadVisible.push(o.id);
                            } else {
                                flatLayer.ensure(o.id, {
                                    url: '/' + o.path + o.filename,
                                    bounds: new google.maps.LatLngBounds(
                                        new google.maps.LatLng(o.lat_southwest, o.lng_southwest),
                                        new google.maps.LatLng(o.lat_northeast, o.lng_northeast)),
                                    opacity: fOpacity
                                });
                                flatVisible.push(o.id);
                            }
                            return;
                        }
                        // Getilt: ImageMapType pro Overlay-Id cachen → Opacity = nur setOpacity, kein remove+re-add
                        // (sonst Tile-Neuladen = Flackern). Geometrie/Zoom sind im Frontend zur Laufzeit konstant.
                        var tid = String(order[i].id);
                        tiledVisible.push(tid);
                        var cachedImt = tiledItems.get(tid);
                        if (cachedImt) { cachedImt.setOpacity(order[i].opacity != null ? order[i].opacity : 1); return; }
                        const visibleOverlay = new google.maps.ImageMapType({
                            getTileUrl: function (c, z) {
                                if (z < order[i].zoom_min || z > order[i].zoom_max) {
                                    return '/statimg/null.png';
                                }
                                var ymax = 1 << z;
                                var y = ymax - c.y - 1;
                                var zfactor = Math.pow(2, z);
                                var bounds = new google.maps.LatLngBounds(new google.maps.LatLng(order[i].lat_southwest, order[i].lng_southwest), new google.maps.LatLng(order[i].lat_northeast, order[i].lng_northeast));
                                var tileBounds = new google.maps.LatLngBounds(
                                    map.getProjection().fromPointToLatLng(new google.maps.Point(c.x * 256 / zfactor, (c.y + 1) * 256 / zfactor)),
                                    map.getProjection().fromPointToLatLng(new google.maps.Point((c.x + 1) * 256 / zfactor, (c.y) * 256 / zfactor))
                                );

                                if (bounds.intersects(tileBounds)) {
                                    return '/' + order[i].path + order[i].filename.split('.')[0] + '/' + z + '/' + c.x + '/' + y + '.png';
                                } else {
                                    return '/statimg/null.png';
                                }
                            },
                            opacity: order[i].opacity,
                            tileSize: new google.maps.Size(256, 256),
                            isPng: true,
                            minZoom: order[i].zoom_min,
                            maxZoom: order[i].zoom_max,
                            name: "customOverlay"
                        });
                        visibleOverlay._customName = "customOverlay";
                        map.overlayMapTypes.push(visibleOverlay);
                        tiledItems.set(tid, visibleOverlay);
                    })(id);
                }
                if (flatLayer) { flatLayer.retain(flatVisible); }
                if (quadLayer) { quadLayer.retain(quadVisible); }
                // Getilte reconcilen: nicht mehr sichtbare ImageMapTypes aus der Karte + Cache entfernen (statt remove-all).
                var keepTiled = new Set(tiledVisible);
                for (var tkey of [...tiledItems.keys()]) {
                    if (keepTiled.has(tkey)) { continue; }
                    var deadImt = tiledItems.get(tkey);
                    for (var mi = map.overlayMapTypes.getLength() - 1; mi >= 0; mi--) {
                        if (map.overlayMapTypes.getAt(mi) === deadImt) { map.overlayMapTypes.removeAt(mi); }
                    }
                    tiledItems.delete(tkey);
                }
            }

            // Store-Änderung (Toggle/Opacity/extern) → neu rendern. Unidirektional: UI schreibt in den Store, nie direkt rendern.
            // unsubscribe in destroy(), weil der Store jetzt zentral ist und createOverlayPanel überlebt (sonst Leak pro Rebuild).
            unsubscribe = store.subscribe(function () { update_ui(); });
            mapTypeListener = google.maps.event.addListener(map, 'maptypeid_changed', update_ui);

            // Store mit View-State seeden → triggert das initiale update_ui.
            seedStore(false);

            // OV-Panel (Button + Liste/Auge/Tools). Auge/Opacity schreiben in den Store (→ update_ui). Items aus Store ⋈ byId.
            if (stamm.length) {
                var buildItems = function () {
                    return store.get().overlays.map(function (vs) {
                        var o = byId[vs.id] || {};
                        var group = (o.source === 'wmts') ? GROUP_EXTERN : GROUP_ARCHIVE;
                        return {
                            id: vs.id, name: o.name, year: o.year, info_text: o.info_text,
                            visible: vs.visible, opacity: vs.opacity, sortOrder: vs.sortOrder,
                            // Gruppe (Sortierung) + fertige Beschriftung — die View rendert nur, was hier steht.
                            group: group,
                            groupLabel: str(group === GROUP_EXTERN ? 'OVERLAY_GROUP_EXTERNAL' : 'OVERLAY_GROUP_ARCHIVE')
                        };
                    }).sort(function (a, b) {
                        // Erst die Gruppe (extern oben), innerhalb der Gruppe wie bisher nach z-Order (oben = 0 = vorne).
                        // ACHTUNG: „oben = vorne" gilt damit nur noch INNERHALB der Archiv-Gruppe — die externen
                        // Kartenwerke stehen oben in der Liste, rendern aber als Basiskarte darunter.
                        var g = (GROUP_RANK[a.group] || 0) - (GROUP_RANK[b.group] || 0);
                        return g !== 0 ? g : (a.sortOrder || 0) - (b.sortOrder || 0);
                    });
                };
                // Liste beim Öffnen des Panels auffrischen. Dieselbe Abfrage wie beim Aufbau — nur die DATEN werden
                // ersetzt (applyStammFromResponse), die Verdrahtung daneben bleibt stehen und läuft weiterhin genau
                // einmal. Ohne das zeigte das Panel bis zum Neuladen der Seite den Stand vom Seitenaufbau: ein im
                // Backend frisch freigeschaltetes Overlay fehlte, ein zurückgezogenes blieb stehen.
                //
                // `seedStore(true)` ist hier Pflicht, nicht Geschmack: der Nutzer hat möglicherweise Overlays
                // eingeblendet und die Deckkraft verstellt. Mit der Aufbau-Regel (`keepOnPositionChange`) würde ein
                // Klick auf den Overlays-Knopf genau das zurückstellen — das Panel öffnen darf nichts verändern.
                //
                // GRENZE, die bleibt: Hatte das Archiv beim Seitenaufbau GAR KEIN Overlay, existiert weder Panel
                // noch Knopf (siehe `if (stamm.length)` weiter oben) — und damit nichts, worüber sich ein Nachladen
                // auslösen liesse. Das allererste Overlay eines Archivs braucht weiterhin ein Neuladen der Seite.
                var refreshInFlight = false;
                var refreshItems = function () {
                    if (destroyed || refreshInFlight) { return; }   // Doppelklick auf den Knopf → eine Abfrage
                    refreshInFlight = true;
                    $.ajax({
                        url: '/includes/ajax.php',
                        cache: false,
                        type: 'POST',
                        data: 'action=get_overlays' + (opts.showAll ? '&all=1' : ''),
                        dataType: 'json'
                    }).done(function (fresh) {
                        if (destroyed || !fresh || !fresh.success || !fresh.data) { return; }
                        applyStammFromResponse(fresh);
                        seedStore(true);
                        var panel = map.__topoOvPanel;
                        if (panel && panel.isAlive && panel.isAlive()) { panel.setItems(buildItems()); }
                    }).always(function () { refreshInFlight = false; });
                };

                // Sprung zur Overlay-Bounds (mit Rand). Bounds aus den Stammdaten (byId).
                var jumpToOverlay = function (id) {
                    var so = byId[id];
                    if (so && so.lat_southwest && so.lng_southwest && so.lat_northeast && so.lng_northeast) {
                        map.fitBounds(new google.maps.LatLngBounds(
                            new google.maps.LatLng(so.lat_southwest, so.lng_southwest),
                            new google.maps.LatLng(so.lat_northeast, so.lng_northeast)), 60);
                    }
                };
                var ovOnToggle = function (id, on) {
                    store.patchOverlay(id, { visible: !!on });
                    // Chef-Charge 4 (2): KEIN Auto-Sprung beim Ein-/Ausblenden mehr (Zeile/Auge togglen nur Sichtbarkeit) —
                    // der Sprung läuft jetzt ausschließlich über den Zentrieren-Button (ovOnSelect). (Wunsch 10 überholt.)
                };
                // Zentrieren-Button (Chef-Charge 4, 2): wählt das Item (Tool-Panel) UND springt zum Overlay.
                var ovOnSelect = function (id) { jumpToOverlay(id); };
                var ovOnOpacity = function (id, frac) { store.patchOverlay(id, { opacity: frac }); };
                // Chef-Wunsch (9): Frontend-Sortierung (memory) — sortOrder = neue Listenposition (oben = 0 = vorne). EIN setOverlays
                // (statt N patchOverlay) → ein update_ui → z-Order folgt. Reihenfolge im Array = orderedIds.
                var ovOnReorder = function (orderedIds) {
                    var byVS = {};
                    store.get().overlays.forEach(function (vs) { byVS[vs.id] = vs; });
                    store.setOverlays(orderedIds.map(function (id, idx) { return Object.assign({}, byVS[id], { sortOrder: idx }); }));
                };
                // {id}-Doc-Link im Infotext → Gallery öffnen. openGallery wird REINGEREICHT (opts.openGallery, aus der
                // Reconciler-API → MapController.openGalleryLegacy), KEIN Global. Im Admin-Kontext undefined → no-op.
                var ovOnOpenDoc = (typeof opts.openGallery === 'function') ? function (did) { opts.openGallery(did); } : null;
                // {ov:id}-Overlay-Link im Infotext → referenziertes Overlay sichtbar schalten + drauf zentrieren.
                // id-Auflösung über byId (data-ov-id ist String → kanonische Store-id so.id); unbekannte/fremde id → no-op.
                var ovOnActivateOverlay = function (id) {
                    var so = byId[id];
                    if (!so) { return; }
                    store.patchOverlay(so.id, { visible: true });   // Store/Karte
                    jumpToOverlay(so.id);                            // hinspringen
                    // Panel-Auge (+ Zentrieren-Button) der Zeile nachziehen — Store-Änderung allein rendert die Liste nicht neu.
                    if (map.__topoOvPanel && map.__topoOvPanel.setItemVisible) { map.__topoOvPanel.setItemVisible(so.id, true); }
                };

                // Pro Karte nur EIN Panel: existiert es noch im Karten-DOM (Button-Guard), nur Daten/Callbacks auffrischen,
                // sonst neu anlegen (stale → vorher destroy).
                var ovMapDiv = map.getDiv ? map.getDiv() : null;
                var ovExisting = map.__topoOvPanel;
                var ovAlive = ovExisting && ovExisting.isAlive && ovExisting.isAlive();
                if (ovAlive) {
                    ovExisting.setItems(buildItems());
                    ovExisting.setCallbacks(ovOnToggle, ovOnSelect, ovOnOpacity, ovOnReorder);
                    // Muss mit: refreshItems schliesst über buildItems DIESES Laufs. Bliebe die alte Closure stehen,
                    // füllte das Nachladen die Liste aus den Daten des vorigen Kartenkontexts (Gallery ↔ Haupt).
                    if (ovExisting.setOnOpen) { ovExisting.setOnOpen(refreshItems); }
                    if (ovExisting.setSide) { ovExisting.setSide(opts.ovSide || 'right'); }
                } else {
                    if (ovExisting && ovExisting.destroy) { ovExisting.destroy(); }
                    map.__topoOvPanel = new (createOverlayControlsClass(google))(map, {
                        side: (opts.ovSide || 'right'), noButton: !!opts.noButton, items: buildItems(),
                        onToggleVisible: ovOnToggle, onSelect: ovOnSelect, onOpacity: ovOnOpacity, onReorder: ovOnReorder,
                        onOpenDoc: ovOnOpenDoc, onActivateOverlay: ovOnActivateOverlay, onOpen: refreshItems
                    });
                }
            }

            if (typeof opts.onReady === 'function') { opts.onReady(show); }
        }
    });

    return {
        // Map-UI-Unify (Chef 16): Panel-Toggle nach außen geben — das Katalog-OV-Item (mapTools, Galerie) ruft es über die api.
        toggle() { if (map.__topoOvPanel && map.__topoOvPanel.toggle) { map.__topoOvPanel.toggle(); } },
        isOpen() { return !!(map.__topoOvPanel && map.__topoOvPanel.isOpen && map.__topoOvPanel.isOpen()); },
        close() { if (map.__topoOvPanel && map.__topoOvPanel.close) { map.__topoOvPanel.close(); } },
        // Vollständiger Teardown: Panel + Layer + getilte customOverlay + maptypeid_changed-Listener. Idempotent/safe
        // auch wenn vor dem ajax-Done aufgerufen (destroyed-Flag verhindert dann den Aufbau).
        destroy() {
            destroyed = true;
            if (unsubscribe) { unsubscribe(); unsubscribe = null; }   // Store überlebt → Subscription lösen
            if (mapTypeListener) { google.maps.event.removeListener(mapTypeListener); mapTypeListener = null; }
            if (map.__topoOvPanel && map.__topoOvPanel.destroy) { map.__topoOvPanel.destroy(); }
            map.__topoOvPanel = null;
            if (map.__topoFlatLayer && map.__topoFlatLayer.clear) { map.__topoFlatLayer.clear(); }
            if (map.__topoQuadLayer && map.__topoQuadLayer.clear) { map.__topoQuadLayer.clear(); }
            if (map.overlayMapTypes) {
                for (let k = map.overlayMapTypes.getLength() - 1; k >= 0; k--) {
                    const omt = map.overlayMapTypes.getAt(k);
                    if (omt && omt._customName === 'customOverlay') { map.overlayMapTypes.removeAt(k); }
                }
            }
        }
    };
}
